import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Chat, Message, ChatContextType } from '../types';
import { StorageService } from '../services/storage/StorageService';

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
    children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const [chatHistory, setChatHistory] = useState<Chat[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState('');
    const [isStreaming] = useState(false);
    const [selectedAssistant, setSelectedAssistant] = useState('Saskatchewan Auto Assistant');
    const [error, setError] = useState<Error | null>(null);
    const [streamingEnabled] = useState(true);

    const storageService = StorageService.getInstance();

    useEffect(() => {
        const history = storageService.getChatHistory();
        setChatHistory(history);
    }, []);

    const addMessage = useCallback((message: Message) => {
        setMessages(prev => [...prev, message]);
    }, []);

    const setLoading = useCallback((loading: boolean) => {
        setIsLoading(loading);
    }, []);

    const startNewChat = useCallback(() => {
        const newChatId = Date.now().toString();
        console.log(selectedAssistant);
        const newChat: Chat = {
            id: newChatId,
            assistant: selectedAssistant,
            title: 'New Chat',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };

        storageService.addChat(newChat);
        setChatHistory(prev => [newChat, ...prev]);
        setCurrentChatId(newChatId);
        setMessages([]);
    }, [selectedAssistant, storageService]);

    const loadChat = useCallback((chatId: string) => {
        const chat = chatHistory.find(c => c.id === chatId);
        if (chat) {
            setMessages(chat.messages || []);
            setCurrentChatId(chatId);
            setSelectedAssistant(chat.assistant);
        }
    }, [chatHistory]);

    const deleteChat = useCallback((chatId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        storageService.deleteChat(chatId);
        setChatHistory(prev => prev.filter(c => c.id !== chatId));

        if (currentChatId === chatId) {
            setMessages([]);
            setCurrentChatId(null);
        }
    }, [currentChatId, storageService]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const sendMessage = useCallback(async (content: string) => {
        // This will be implemented in the useChat hook
        console.log('sendMessage called with:', content);
    }, []);

    const value: ChatContextType = {
        messages,
        currentChatId,
        chatHistory,
        isLoading,
        streamingMessage,
        isStreaming,
        selectedAssistant,
        error,
        streamingEnabled,
        sendMessage,
        startNewChat,
        loadChat,
        deleteChat,
        setSelectedAssistant,
        clearError,
        addMessage,
        setLoading,
        setStreamingMessage,
        setError,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};